import ISearchParams from '@shared/models/ISearchParams';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAlertsStore } from './state/AlertsContainer';
import { useOrganizationStore } from './state/OrganizationContainer';
import useScrollToElement from './useScrollToElement';

export default function useManagedOrgsSearch() {
    const { t } = useTranslation();
    const { organizationId } = useParams<{ organizationId: string }>();
    const orgManagedOrgs = useOrganizationStore((state) => state.orgManagedOrgs);
    const isLoading = useOrganizationStore((state) => state.loadingData.managedOrgs);
    const managedOrgsSearchContentId = 'managed-orgs-search';
    const defaultTake = 51;
    const displayLimit = defaultTake - 1;
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedQuery, setDebouncedQuery] = useState<ISearchParams>({
        searchText: '',
        skip: 0,
        take: defaultTake,
    });

    useScrollToElement(managedOrgsSearchContentId, [debouncedQuery.skip]);

    function handleSearchQueryChange(value: string) {
        setSearchQuery(value);
    }

    const showPrevious = useMemo(() => debouncedQuery.skip && debouncedQuery.skip > 0, [debouncedQuery.skip]);
    const showNext = useMemo(() => orgManagedOrgs.length >= displayLimit, [orgManagedOrgs]);

    const DEBOUNCE_DELAY = 500;

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const newQuery = {
                searchText: searchQuery.toLowerCase(),
                skip: 0,
                take: defaultTake,
            };
            if (newQuery.searchText !== debouncedQuery.searchText) {
                setDebouncedQuery(newQuery);
            }
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const handleNext = () => {
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: displayLimit + (prev.skip ?? 0),
        }));
    };

    const handlePrevious = () => {
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: Math.max(0, (prev.skip ?? 0) - displayLimit),
        }));
    };

    useEffect(() => {
        const trySearch = async () => {
            if (useOrganizationStore.getState().loadingData.managedOrgs) return;
            if (!organizationId) return;
            try {
                useOrganizationStore.getState().handleLoadingDataChange('managedOrgs', true);
                await useOrganizationStore.getState().getOrgManagedOrgsAll(organizationId, debouncedQuery);
            } catch (e) {
                useAlertsStore.getState().showError(t('common:organization.managed.error'));
            } finally {
                useOrganizationStore.getState().handleLoadingDataChange('managedOrgs', false);
            }
        };
        trySearch();
    }, [debouncedQuery]);

    return {
        results: orgManagedOrgs,
        managedOrgsSearchContentId,
        handleSearchQueryChange,
        searchQuery,
        showPrevious,
        showNext,
        handleNext,
        handlePrevious,
        isLoading,
    };
}
